import { useState } from "react";
import UsersView from "../components/UsersView";
import { Button, Col, Modal, Row, Space, Switch, Typography, message } from "antd";
import { Guid } from "guid-typescript";
import { SearchQuery } from "../code/search-query/search-query";
import {
  getArchivedUsers,
  getPlatformUsersExport,
  restoreUser,
} from "../repository/data.repository";
import Loader from "../controls/Loader";
import UserView from "../components/UserView";
import { PlatformUser } from "../repository/data.default";
import { UserForm } from "../components/UserView.default";
import ArchivedListView from "../controls/ArchivedListView";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getProblemMessage } from "../utils/http";

function UsersPage() {
  const [modal, contextHolder] = Modal.useModal();
  const permissions = useSelector(authorizeSelectors.permissions);
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [userInputMode, setUserInputMode] = useState<"delete" | "view">(
    "delete",
  );
  const [userInputValues, setUserInputValues] = useState<UserForm>({} as any);
  const [userInputOpen, setUserInputOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Events

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onUserInputOpen(
    mode: "view" | "delete",
    values?: PlatformUser,
  ): void {
    setUserInputMode(mode);
    setUserInputOpen(true);
    setUserInputValues(
      values
        ? {
            uid: values.uid,
            userName: values.userName,
          }
        : ({} as any),
    );
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading users export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getPlatformUsersExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  function onUserFinish(): void {
    setUserInputOpen(false);
    setTriggerView(Guid.create().toString());
  }

  function onUserClose(): void {
    setUserInputOpen(false);
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Users
        </Typography.Title>
        <Space className="flex flex-row">
          {/* Admin brief 2026-11-03 — Show archived toggle (soft-delete view). */}
          <Typography.Text type="secondary">Show archived</Typography.Text>
          <Switch
            size="small"
            checked={showArchived}
            onChange={setShowArchived}
          />
          <Button
            type="primary"
            disabled={!permissions.can("PlatformUsers", "view")}
            onClick={() => onDownloadExport()}
          >
            Export to Excel
          </Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        {showArchived ? (
          <ArchivedListView<PlatformUser>
            entityLabel="user"
            loader={getArchivedUsers as any}
            restore={restoreUser}
            rowKey={(row) => row.uid}
            columns={[
              { title: "User Name", dataIndex: "userName", width: 180 },
              { title: "First Name", dataIndex: "firstName", width: 160 },
              { title: "Last Name", dataIndex: "lastName", width: 160 },
              { title: "Email", dataIndex: "email", width: 220 },
              {
                title: "Deactivated",
                dataIndex: "deactivatedAt",
                width: 160,
                render: (v?: string | null) =>
                  v ? new Date(v).toLocaleString("en-GB") : "—",
              },
            ]}
          />
        ) : (
          <UsersView
            trigger={triggerView}
            onSearch={onSearch}
            onAction={(key: string, record: any) =>
              onUserInputOpen(key as any, record)
            }
          />
        )}
      </Col>
      <UserView
        open={userInputOpen}
        mode={userInputMode}
        uid={userInputValues.uid}
        onFinish={onUserFinish}
        onClose={onUserClose}
      />
      {contextHolder}
    </Col>
  );
}

export default UsersPage;
